import React from "react";
import { Check, X, Car, Sofa } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const SectionOverline = ({ children }) => (
  <div className="font-serif italic text-[color:var(--hd-gold)] text-sm md:text-base mb-3">
    — {children}
  </div>
);

const AutoPackages = () => {
  const { t } = useLang();
  const pkgs = t.services.auto.packages;
  const scroll = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="mt-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Car size={20} className="text-[color:var(--hd-gold)]" />
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
              {t.services.auto.title}
            </h3>
          </div>
          <p className="text-white/50 mt-2 text-sm">{t.services.auto.subtitle}</p>
        </div>
        <div className="font-body text-xs uppercase tracking-wider text-[color:var(--hd-gold)]">
          {t.services.auto.suv}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5" data-testid="auto-packages">
        {pkgs.map((p, i) => (
          <div
            key={i}
            className={`card-hover rounded-3xl p-7 relative flex flex-col ${
              p.badge ? "md:scale-[1.03] md:-translate-y-1 !border-[color:var(--hd-gold)]/40" : ""
            }`}
            data-testid={`auto-package-${p.name.toLowerCase()}`}
          >
            {p.badge && (
              <div className="absolute -top-3 left-7 bg-[color:var(--hd-gold)] text-black text-[10px] font-display font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                {p.badge}
              </div>
            )}
            <div className="flex items-baseline justify-between mb-6">
              <div className="font-display text-2xl font-bold text-white uppercase tracking-wide">
                {p.name}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold gold-gradient">
                  {p.price}
                </span>
                <span className="text-[color:var(--hd-gold)] text-lg">€</span>
              </div>
            </div>
            <div className="gold-divider mb-5" />
            <ul className="space-y-3 mb-5 flex-1">
              {p.features.map((f, j) => (
                <li key={j} className="flex items-start gap-3 text-sm text-white/75">
                  <Check
                    size={16}
                    className="text-[color:var(--hd-gold)] mt-0.5 shrink-0"
                  />
                  <span>{f}</span>
                </li>
              ))}
              {p.excludes?.map((e, j) => (
                <li
                  key={`x-${j}`}
                  className="flex items-start gap-3 text-sm text-white/35 line-through"
                >
                  <X size={16} className="text-white/20 mt-0.5 shrink-0" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={scroll}
              className="btn-outline w-full justify-center"
              data-testid={`book-auto-${p.name.toLowerCase()}`}
            >
              {t.nav.cta}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h4 className="font-display text-xl font-bold text-white mb-5">
          {t.services.addons.title}
        </h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="auto-addons">
          {t.services.addons.items.map((a, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-5 py-4 rounded-xl border border-white/5 bg-[#141414] hover:border-[color:var(--hd-gold)]/30 transition-colors"
            >
              <span className="text-sm text-white/80">{a.name}</span>
              <span className="font-display font-bold text-[color:var(--hd-gold)]">
                {a.price}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FurnitureGrid = () => {
  const { t } = useLang();
  return (
    <div className="mt-20">
      <div className="flex items-center gap-3 mb-3">
        <Sofa size={20} className="text-[color:var(--hd-gold)]" />
        <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
          {t.services.furniture.title}
        </h3>
      </div>
      <p className="text-white/50 mb-8 text-sm">{t.services.furniture.subtitle}</p>
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
        data-testid="furniture-groups"
      >
        {t.services.furniture.groups.map((g, i) => (
          <div
            key={i}
            className="card-hover rounded-2xl p-6 flex flex-col"
            data-testid={`furniture-group-${i}`}
          >
            <div className="font-display text-lg font-bold text-white mb-4 uppercase tracking-wide">
              {g.label}
            </div>
            <div className="gold-divider mb-4" />
            <ul className="space-y-3 flex-1">
              {g.items.map((it, j) => (
                <li
                  key={j}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-white/75">{it.name}</span>
                  <span className="font-display font-bold text-[color:var(--hd-gold)]">
                    {it.price}
                  </span>
                </li>
              ))}
            </ul>
            {g.note && (
              <div className="mt-4 text-[11px] text-white/40 italic">
                {g.note}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="services"
      className="py-24 md:py-32 relative"
      data-testid="services-section"
    >
      <div className="container-hd">
        <div className="max-w-3xl mb-6">
          <SectionOverline>{t.services.overline}</SectionOverline>
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white tracking-tight leading-tight">
            {t.services.title}
          </h2>
          <p className="text-white/60 mt-5 text-base md:text-lg max-w-2xl">
            {t.services.subtitle}
          </p>
        </div>

        <AutoPackages />
        <FurnitureGrid />
      </div>
    </section>
  );
};

export default Services;
